import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
    Loader2,
    MessageCircleQuestion,
    Star,
    Send,
    CheckCircle2,
    Clock,
} from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/lib/clientAuth";
import PortalShell from "@/components/PortalShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientHelp() {
    const { client, token, loading } = useClientAuth();
    const [qBusy, setQBusy] = useState(true);
    const [questions, setQuestions] = useState([]);
    const [feedback, setFeedback] = useState([]);
    const [projects, setProjects] = useState([]);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token],
    );

    // Question form
    const [qBody, setQBody] = useState("");
    const [qProject, setQProject] = useState("");
    const [qSubmitting, setQSubmitting] = useState(false);

    // Feedback form
    const [fKind, setFKind] = useState("overall");
    const [fProject, setFProject] = useState("");
    const [fRating, setFRating] = useState(0);
    const [fHover, setFHover] = useState(0);
    const [fBody, setFBody] = useState("");
    const [fSubmitting, setFSubmitting] = useState(false);

    const refresh = () => {
        setQBusy(true);
        return Promise.all([
            axios.get(`${API}/client/questions`, { headers }),
            axios.get(`${API}/client/feedback`, { headers }),
            axios.get(`${API}/client/projects`, { headers }),
        ])
            .then(([q, f, p]) => {
                setQuestions(q.data || []);
                setFeedback(f.data || []);
                setProjects(p.data || []);
            })
            .catch(() => toast.error("Could not load help center"))
            .finally(() => setQBusy(false));
    };

    useEffect(() => {
        if (token) refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (loading)
        return (
            <div className="min-h-screen bg-ink flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ember" />
            </div>
        );
    if (!client) return <Navigate to="/portal/login" replace />;

    const submitQuestion = async (e) => {
        e.preventDefault();
        if (!qBody.trim()) {
            toast.error("Type your question first");
            return;
        }
        setQSubmitting(true);
        try {
            await axios.post(
                `${API}/client/questions`,
                { body: qBody, project_id: qProject || null },
                { headers },
            );
            toast.success("Question sent — we'll be in touch shortly");
            setQBody("");
            setQProject("");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send question");
        } finally {
            setQSubmitting(false);
        }
    };

    const submitFeedback = async (e) => {
        e.preventDefault();
        if (!fRating && !fBody.trim()) {
            toast.error("Pick a rating or write a comment (or both)");
            return;
        }
        if (fKind === "project" && !fProject) {
            toast.error("Pick the project this feedback is about");
            return;
        }
        setFSubmitting(true);
        try {
            await axios.post(
                `${API}/client/feedback`,
                {
                    kind: fKind,
                    project_id: fKind === "project" ? fProject : null,
                    rating: fRating || null,
                    body: fBody || null,
                },
                { headers },
            );
            toast.success("Thanks for the feedback ✦");
            setFRating(0);
            setFBody("");
            setFProject("");
            setFKind("overall");
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send feedback");
        } finally {
            setFSubmitting(false);
        }
    };

    return (
        <PortalShell>
            <div className="max-w-5xl">
                <p className="text-xs uppercase tracking-[0.25em] text-ember mb-3">
                    Help center
                </p>
                <h1 className="font-display text-bone text-5xl sm:text-6xl tracking-tightest leading-[0.95] mb-12">
                    Ask us
                    <span className="font-serif-i italic text-ember"> anything.</span>
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left: Q&A */}
                    <section className="lg:col-span-7">
                        <div className="flex items-center gap-2 mb-5">
                            <MessageCircleQuestion className="w-5 h-5 text-ember" />
                            <h2 className="font-display text-bone text-2xl tracking-tight">
                                Questions
                            </h2>
                        </div>

                        <form
                            onSubmit={submitQuestion}
                            data-testid="question-form"
                            className="border border-white/10 bg-ink-800/40 p-6 mb-8 space-y-4"
                        >
                            <textarea
                                value={qBody}
                                onChange={(e) => setQBody(e.target.value)}
                                rows={3}
                                placeholder="What's on your mind? Billing, scheduling, scope, anything…"
                                data-testid="question-body"
                                className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                            />
                            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                                <select
                                    value={qProject}
                                    onChange={(e) => setQProject(e.target.value)}
                                    data-testid="question-project"
                                    className="bg-ink border border-white/10 focus:border-ember outline-none px-3 py-2 text-bone text-xs uppercase tracking-[0.18em] sm:max-w-[260px]"
                                >
                                    <option value="">General question</option>
                                    {projects.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            About: {p.title}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="submit"
                                    disabled={qSubmitting}
                                    data-testid="question-submit"
                                    className="btn-ember inline-flex items-center justify-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                                >
                                    {qSubmitting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Send question
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>

                        {qBusy ? (
                            <div className="text-bone-500 text-sm flex items-center gap-3">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading…
                            </div>
                        ) : questions.length === 0 ? (
                            <p className="text-bone-500 text-sm border border-dashed border-white/10 p-8 text-center">
                                No questions yet. Ask us anything above.
                            </p>
                        ) : (
                            <ul
                                data-testid="questions-list"
                                className="space-y-4"
                            >
                                {questions.map((q) => (
                                    <li
                                        key={q.id}
                                        data-testid={`question-${q.id}`}
                                        className="border border-white/10 bg-ink-800/40 p-5"
                                    >
                                        <div className="flex items-center justify-between gap-3 mb-3">
                                            <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                                {q.project_title
                                                    ? `About: ${q.project_title}`
                                                    : "General"}
                                            </span>
                                            <span
                                                className={`text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1 px-2 py-1 border ${
                                                    q.status === "answered"
                                                        ? "border-emerald-400/40 text-emerald-400"
                                                        : "border-amber-400/40 text-amber-400"
                                                }`}
                                            >
                                                {q.status === "answered" ? (
                                                    <CheckCircle2 className="w-3 h-3" />
                                                ) : (
                                                    <Clock className="w-3 h-3" />
                                                )}
                                                {q.status}
                                            </span>
                                        </div>
                                        <p className="text-bone text-sm leading-relaxed whitespace-pre-wrap">
                                            {q.body}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-[0.2em] text-bone-500 mt-2">
                                            {new Date(q.created_at).toLocaleString()}
                                        </p>
                                        {q.answer_body && (
                                            <div className="mt-4 border-l-2 border-ember pl-4">
                                                <p className="text-[10px] uppercase tracking-[0.2em] text-ember mb-2">
                                                    Aximo Studios ·{" "}
                                                    {q.answered_at
                                                        ? new Date(
                                                              q.answered_at,
                                                          ).toLocaleString()
                                                        : ""}
                                                </p>
                                                <p className="text-bone-300 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {q.answer_body}
                                                </p>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    {/* Right: Feedback */}
                    <section className="lg:col-span-5">
                        <div className="flex items-center gap-2 mb-5">
                            <Star className="w-5 h-5 text-ember" />
                            <h2 className="font-display text-bone text-2xl tracking-tight">
                                Feedback
                            </h2>
                        </div>

                        <form
                            onSubmit={submitFeedback}
                            data-testid="feedback-form"
                            className="border border-white/10 bg-ink-800/40 p-6 mb-8 space-y-5"
                        >
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { v: "overall", label: "Studio overall" },
                                    { v: "project", label: "Specific project" },
                                ].map((opt) => (
                                    <button
                                        type="button"
                                        key={opt.v}
                                        onClick={() => setFKind(opt.v)}
                                        data-testid={`feedback-kind-${opt.v}`}
                                        className={`px-3 h-10 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                            fKind === opt.v
                                                ? "border-ember text-ember"
                                                : "border-white/15 text-bone-300 hover:border-white/30"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>

                            {fKind === "project" && (
                                <select
                                    value={fProject}
                                    onChange={(e) => setFProject(e.target.value)}
                                    data-testid="feedback-project"
                                    className="w-full bg-ink border border-white/10 focus:border-ember outline-none px-3 py-2 text-bone text-xs uppercase tracking-[0.18em]"
                                >
                                    <option value="">Pick a project…</option>
                                    {projects.map((p) => (
                                        <option key={p.id} value={p.id}>
                                            {p.title}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Rating (optional)
                                </p>
                                <div
                                    className="flex items-center gap-1"
                                    data-testid="feedback-rating"
                                >
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button
                                            type="button"
                                            key={n}
                                            onClick={() =>
                                                setFRating(fRating === n ? 0 : n)
                                            }
                                            onMouseEnter={() => setFHover(n)}
                                            onMouseLeave={() => setFHover(0)}
                                            data-testid={`feedback-star-${n}`}
                                            className="p-1"
                                        >
                                            <Star
                                                className={`w-7 h-7 transition-colors ${
                                                    n <= (fHover || fRating)
                                                        ? "fill-ember text-ember"
                                                        : "text-bone-500"
                                                }`}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <textarea
                                value={fBody}
                                onChange={(e) => setFBody(e.target.value)}
                                rows={4}
                                placeholder="What's working, what isn't, what would make this great…"
                                data-testid="feedback-body"
                                className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                            />

                            <button
                                type="submit"
                                disabled={fSubmitting}
                                data-testid="feedback-submit"
                                className="btn-ember w-full inline-flex items-center justify-center gap-2 h-11 px-5 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
                            >
                                {fSubmitting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <>Send feedback</>
                                )}
                            </button>
                        </form>

                        {feedback.length > 0 && (
                            <>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3">
                                    Your past feedback
                                </p>
                                <ul
                                    data-testid="feedback-list"
                                    className="space-y-3"
                                >
                                    {feedback.map((f) => (
                                        <li
                                            key={f.id}
                                            className="border border-white/10 bg-ink-800/40 p-4"
                                        >
                                            <div className="flex items-center justify-between gap-3 mb-2">
                                                <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                                    {f.project_title
                                                        ? `About: ${f.project_title}`
                                                        : "Studio overall"}
                                                </span>
                                                <span className="text-[10px] uppercase tracking-[0.2em] text-bone-500">
                                                    {new Date(
                                                        f.created_at,
                                                    ).toLocaleDateString()}
                                                </span>
                                            </div>
                                            {f.rating && (
                                                <div className="flex items-center gap-1 mb-2">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Star
                                                            key={n}
                                                            className={`w-4 h-4 ${
                                                                n <= f.rating
                                                                    ? "fill-ember text-ember"
                                                                    : "text-bone-500"
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            {f.body && (
                                                <p className="text-bone-300 text-sm leading-relaxed">
                                                    {f.body}
                                                </p>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </section>
                </div>
            </div>
        </PortalShell>
    );
}
